import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { Confetti, playCorrect, playWrong } from "@/components/Bits";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

const DURATION = 30;
const GOAL = 12;

export default function CatchStars() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("intro");
  const [stars, setStars] = useState([]);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const idRef = useRef(0);

  useEffect(() => {
    if (phase !== "play") return;
    const spawn = setInterval(() => {
      const id = ++idRef.current;
      const cloud = Math.random() < 0.2;
      setStars(s => [...s, { id, x: 5 + Math.random() * 85, dur: 3.2 + Math.random() * 2, cloud }]);
      setTimeout(() => setStars(s => s.filter(st => st.id !== id)), 6000);
    }, 650);
    const tick = setInterval(() => setTime(t => t - 1), 1000);
    return () => { clearInterval(spawn); clearInterval(tick); };
  }, [phase]);

  useEffect(() => {
    if (phase === "play" && time <= 0) {
      setPhase("done"); setStars([]); window.scrollTo({ top: 0, behavior: "smooth" });
      const win = score >= GOAL;
      completeActivity("games", "catch-stars", win ? 5 : 1);
      speak(win ? `Wow! You caught ${score} stars!` : `You caught ${score} stars. Try again for more!`);
    }
  }, [time, phase, score]);

  const catchStar = (st) => {
    setStars(s => s.filter(x => x.id !== st.id));
    if (st.cloud) { playWrong(); recordAnswer("games", false); setScore(s => Math.max(0, s - 1)); }
    else { playCorrect(); recordAnswer("games", true); setScore(s => s + 1); }
  };
  const start = () => { setScore(0); setTime(DURATION); setStars([]); setPhase("play"); speak("Catch the stars! Avoid the clouds!"); };

  return (
    <>
      <TopBar title="Catch the Stars" backTo="/play" />
      <div className="ll-bg min-h-screen pb-28">
        <div className="max-w-3xl mx-auto px-4 pt-4">
          {phase === "intro" && (
            <div className="ll-card p-8 text-center ll-pop" data-testid="catch-intro">
              <img src="/images/hero_catch_stars.png" alt="" className="w-48 mx-auto ll-float" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: "⭐", className: "text-8xl" })); }} />
              <h1 className="font-display font-bold text-3xl sm:text-4xl text-purple-900 mt-3">Catch the Stars!</h1>
              <p className="text-purple-700 mt-2">Tap the falling ⭐ before they disappear. Avoid the ☁️ clouds! Catch {GOAL} in {DURATION} seconds.</p>
              <button data-testid="catch-start-button" onClick={start} className="ll-btn ll-btn-primary mt-6 text-lg">Start!</button>
            </div>
          )}
          {phase === "play" && (
            <>
              <div className="flex items-center justify-between ll-card px-5 py-3">
                <div className="font-display font-bold text-2xl text-purple-900" data-testid="catch-score">⭐ {score}</div>
                <div className={`font-display font-bold text-2xl ${time <= 5 ? "text-pink-500" : "text-purple-700"}`} data-testid="catch-timer">⏱ {time}s</div>
              </div>
              <div className="relative mt-4 h-[60vh] min-h-[380px] rounded-3xl overflow-hidden border-4 border-white shadow-xl" style={{ background: "linear-gradient(180deg,#312E81 0%,#6D28D9 60%,#C4B5FD 100%)" }} data-testid="catch-arena">
                {stars.map(st => (
                  <button key={st.id} data-testid={st.cloud ? "falling-cloud" : "falling-star"} onPointerDown={() => catchStar(st)}
                    className="absolute -top-16 text-5xl sm:text-6xl select-none cursor-pointer active:scale-125"
                    style={{ left: `${st.x}%`, animation: `ll-fall ${st.dur}s linear forwards`, filter: st.cloud ? "none" : "drop-shadow(0 0 12px #FDE047)" }}>
                    {st.cloud ? "☁️" : "⭐"}
                  </button>
                ))}
              </div>
            </>
          )}
          {phase === "done" && (
            <div className="ll-card p-8 text-center ll-pop" data-testid="done-card">
              <Confetti show={score >= GOAL} />
              <div className="text-6xl">{score >= GOAL ? "🏆" : "🌟"}</div>
              <h2 className="font-display font-bold text-3xl text-purple-900 mt-2">{score >= GOAL ? "Star Catcher!" : "Nice try!"}</h2>
              <p className="text-purple-700 mt-1" data-testid="catch-final-score">You caught {score} stars{score >= GOAL ? " · +5 bonus ⭐" : ` · goal was ${GOAL}`}</p>
              <button onClick={start} data-testid="play-again-button" className="ll-btn ll-btn-primary mt-4">Play again</button>
              <button onClick={() => nav("/play")} data-testid="back-to-play-button" className="ll-btn ll-btn-secondary mt-3 block mx-auto">Back to games</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
